import newsImage1 from "@assets/images/Rectangle-49@2x.png";
import newsImage2 from "@assets/images/Rectangle-491@2x.png";
import newsImage3 from "@assets/images/Rectangle-492@2x.png";
import { Reveal } from "@components/ui";

/* ─────────────────────────────────────────────────────────
   News Card
   ───────────────────────────────────────────────────────── */

interface NewsCardProps {
  image: string;
  date: string;
  category: string;
  title: string;
  featured?: boolean;
}

function NewsCard({ image, date, category, title, featured }: NewsCardProps) {
  if (featured) {
    return (
      <article className="group cursor-pointer relative h-full rounded-[12px] overflow-hidden">
        {/* Full-bleed image — flows naturally to set height */}
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

        {/* Content at bottom */}
        <div className="absolute inset-x-0 bottom-0 p-3.5 md:p-4 lg:p-5 flex flex-col gap-1.5 md:gap-2">
          <div className="flex items-center gap-2.5">
            <span className="rounded-full px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-[11px] md:text-[12px] font-semibold tracking-[-0.02em]">
              {category}
            </span>
            <span className="text-[12px] md:text-[13px] text-white/70 font-albert-sans">{date}</span>
          </div>
          <h3 className="text-[16px] md:text-[18px] lg:text-[22px] tracking-[-0.04em] font-semibold text-white font-albert-sans leading-[1.2]">
            {title}
          </h3>
          <span className="flex items-center gap-2 text-[13px] md:text-[14px] font-semibold text-white/80 group-hover:text-white font-albert-sans transition-all duration-300">
            Read article
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
              <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      </article>
    );
  }

  return (
    <article className="group cursor-pointer h-full bg-white rounded-[12px] overflow-hidden border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300">
      {/* Image */}
      <div className="w-full overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full aspect-[16/9] object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>

      {/* Content */}
      <div className="p-3 md:p-4 flex flex-col gap-2">
        {/* Meta */}
        <div className="flex items-center gap-2.5">
          <span className="rounded-full px-2.5 py-0.5 bg-[#122d63]/[0.08] text-[#122d63] text-[11px] md:text-[12px] font-semibold tracking-[-0.02em] font-albert-sans">
            {category}
          </span>
          <span className="text-[12px] md:text-[13px] text-[#8b8d98] font-albert-sans">{date}</span>
        </div>

        {/* Title */}
        <h3 className="text-[14px] md:text-[15px] tracking-[-0.03em] font-semibold text-[#1e1f24] font-albert-sans leading-[1.3]">
          {title}
        </h3>

        {/* Read more */}
        <span className="mt-auto flex items-center gap-1 text-[12px] md:text-[13px] font-semibold text-[#122d63] font-albert-sans opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
          Read more
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </article>
  );
}

const newsItems = [
  {
    image: newsImage1,
    date: "Nov 20, 2025",
    category: "Residential",
    title: "The anatomy of Gas outlet structure for residential",
    featured: true,
  },
  {
    image: newsImage2,
    date: "Nov 20, 2025",
    category: "Safety",
    title: "Essential gas safety tips every homeowner should know",
  },
  {
    image: newsImage3,
    date: "Nov 20, 2025",
    category: "Infrastructure",
    title: "How smart meters are transforming gas consumption monitoring",
  },
];

/* ─────────────────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────────────────── */

export function NewsSection() {
  const featured = newsItems.find((n) => n.featured);
  const rest = newsItems.filter((n) => !n.featured);

  return (
    <section className="relative w-full bg-[#f6f6f9]">
      <div className="px-5 md:px-10 lg:px-[180px] py-10 md:py-14 lg:py-[72px]">
        <div className="w-full max-w-[1368px] mx-auto flex flex-col gap-6 md:gap-8 lg:gap-10">
          {/* Header */}
          <Reveal direction="up">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-5">
              <div className="flex flex-col gap-3 md:gap-4">
                <span className="inline-block w-fit px-4 py-1.5 rounded-full bg-[#122d63]/[0.08] text-[#122d63] text-[12px] md:text-[13px] font-semibold tracking-[-0.02em] uppercase font-albert-sans">
                  Latest Updates
                </span>
                <h2 className="max-w-[600px] text-[22px] md:text-[34px] lg:text-[44px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
                  News & Insights
                </h2>
              </div>

              <button className="group/btn flex items-center gap-2 py-2.5 pl-5 pr-3 bg-[#090d2c] rounded-[40px] hover:bg-[#1a1f45] transition-colors shrink-0">
                <span className="text-[14px] md:text-[16px] tracking-[-0.02em] font-medium text-white font-albert-sans">
                  View all articles
                </span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white transition-transform duration-300 group-hover/btn:translate-x-1">
                  <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </Reveal>

          {/* Bento-style grid: featured (large left) + 2 small right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
            {/* Featured card — spans full height on desktop */}
            {featured && (
              <Reveal direction="up" className="h-full">
                <NewsCard {...featured} />
              </Reveal>
            )}

            {/* Side column — 2 stacked cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 md:gap-5">
              {rest.map((item, index) => (
                <Reveal key={index} direction="up" delay={100 + index * 100} className="h-full">
                  <NewsCard {...item} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
